<script setup lang="ts">
import type { Sentiment } from '@year13/kui/types/feedback';

import { Button } from '~ds/components/ui/button';
import KUIUnified404 from '~kui/components/KUI/Unified/404/KUIUnified404.vue';
import KUIUnifiedOverview from '~kui/components/KUI/Unified/Overview/KUIUniOverview.vue';
import KUIUnifiedSentiment from '~kui/components/KUI/Unified/Sentiment/KUIUnifiedSentiment.vue';

import type { CollectionMentor } from '~/types/unified';

import ActivitySurveyScreen from '~/components/Unified/Activities/ActivitySurveyScreen.vue';
import LearningAssetCompletionScreen from '~/components/Unified/Activities/LearningAssetCompletionScreen.vue';
import { useActivityOrchestrator } from '~/composables/unified/useActivityOrchestrator';
import { useActivitySurveyScreen } from '~/composables/unified/useActivitySurveyScreen';
import { useIaBackFrame } from '~/composables/unified/useIaBackFrame';
import { useActivityScreenClaim } from '~/composables/unified/useImmersiveActivity';
import { useLearningAssetFlow } from '~/composables/unified/useLearningAssetFlow';
import { useRewardsSuppressed } from '~/composables/unified/useRewardsSuppressed';
import { useShellClosesActivity } from '~/composables/unified/useShellClosesActivity';
import { spotlightOnShow, useSpotlight } from '~/composables/unified/useSpotlight';
import { useSpotlightFlag } from '~/composables/unified/useSpotlightFlag';
import { useUnifiedErrorHandler } from '~/composables/unified/useUnifiedErrorHandler';
import { useExperiment } from '~/composables/useExperiment';
import { AnalyticsOrigin } from '~/constants/analyticsOrigin';
import { EVENTS } from '~/constants/events';
import { GHOST_COMPLETION_EXPERIMENT } from '~/constants/experiments';
import { getLearningAssetContentComponent } from '~/utils/unified/learningAssetRegistry';
import { mapFeedbackSurveyQuestions } from '~/utils/unified/mapFeedbackSurveyQuestions';
import { assetSpotlightTarget } from '~/utils/unified/mapSpotlight';

interface Props {
  activityArgs: {
    learningAssetId?: string;
    learningAssetSlug?: string;
    collectionId?: string;
    collectionName?: string;
    collectionSlug?: string;
    completedCount?: number;
    totalCount?: number;
    mentor?: CollectionMentor | null;
    origin?: AnalyticsOrigin;
    skipOverview?: boolean;
    /**
     * The coach's survey card arriving: this activity is already finished, so the questionnaire
     * standing on it is the only screen to show.
     */
    initialScreen?: 'survey';
  };
  isDevMode?: boolean;
}

const props = defineProps<Props>();

/**
 * What became of the identifier this was mounted for, said once the lookup below has an answer:
 * `true` when it names an asset, `false` when it names nothing.
 *
 * It exists for the coach route that hosts this activity, which has to tell the coach which asset
 * the student is looking at and nothing at all when the link was dead. That page has no lookup of
 * its own — the only thing that turns an asset slug into an asset is `useLearningAssetFlow`, and it
 * is also what starts the content and reports the student's progress in it, so a second copy would
 * open and report the sitting twice. The answer this already holds is handed back instead.
 */
const emit = defineEmits<{ resolved: [found: boolean] }>();

const accessibilityStore = useAccessibilityStore();
const identifier = computed(() => props.activityArgs.learningAssetSlug ?? props.activityArgs.learningAssetId ?? '');
const bySlug = computed(() => !!props.activityArgs.learningAssetSlug);
const isInsideCollection = computed(() => !!props.activityArgs.collectionId);
const spotlightFlag = useSpotlightFlag();
const {
  asset,
  assetSurvey,
  assetSurveyCoinsAwarded,
  baseAssetEventProps,
  learningSessionId,
  screen,
  showCloseConfirmation,
  completionResult,
  fetching,
  stale,
  completing,
  error,
  lastNodeId,
  handleBeginContent,
  starting,
  handleContentComplete,
  handleSentimentComplete,
  handleSurveyDone,
  openSurveyFromCard,
  handleCompletionDone,
  handleCloseRequest,
  confirmClose,
  trackConsiderationsViewed,
  trackClarityViewed,
} = useLearningAssetFlow(identifier, {
  skipOverview: isInsideCollection.value || !!props.activityArgs.skipOverview,
  // A collection opens its activities straight into the content, but the overview is the only
  // place an activity's spotlight shows before the end, so one with a link to offer keeps it.
  keepOverview: loaded => isInsideCollection.value
    && !!spotlightOnShow({ spotlight: loaded.spotlight, flagEnabled: spotlightFlag.value })?.links.length,
  bySlug: bySlug.value,
  origin: props.activityArgs.origin,
  initialScreen: props.activityArgs.initialScreen,
});

// The activity's own spotlight sits on its overview, the way a collection's sits on the
// collection's. The origin names the placement, not where the activity was opened from.
const { spotlight: assetSpotlight, trackLinkClick: trackSpotlightLinkClick } = useSpotlight({
  source: computed(() => asset.value
    ? {
        target: assetSpotlightTarget(asset.value),
        spotlight: asset.value.spotlight,
      }
    : null),
  origin: AnalyticsOrigin.ActivityOverview,
});

// The card can be tapped while this activity's credenza is already mounted, which flips
// `?screen=survey` without a remount.
watch(() => props.activityArgs.initialScreen, (next) => {
  if (next === 'survey') {
    openSurveyFromCard();
  }
});

/**
 * What is on the screen, decided once. The template branches on it and the watcher below reports it,
 * so the answer the coach is given is the answer the student is looking at — there is no second
 * reading of the same refs to disagree with the first.
 *
 * The order is the screen's own. An error outranks a cached asset because that is what is drawn: a
 * revalidation that comes back an error puts the 404 up over the stale copy, and a student looking at
 * a not-found has not visited the content behind it.
 *
 * `stale` is why the miss is not simply "not fetching and nothing here". Under `cache-and-network`
 * the cache answers first and urql ends `fetching` on that answer, so a slug the cache holds as
 * absent lands here as an empty, settled read while the network request that will produce the asset
 * is still out. Called a miss then, it is a miss the arriving asset never takes back.
 */
type LearningAssetScreenState = 'loading' | 'missing' | 'asset';

const screenState = computed<LearningAssetScreenState>(() => {
  if (fetching.value && !asset.value) {
    return 'loading';
  }
  if (error.value) {
    return 'missing';
  }
  if (asset.value) {
    return 'asset';
  }

  return stale.value ? 'loading' : 'missing';
});

watch(screenState, (state) => {
  if (state === 'asset') {
    emit('resolved', true);
    return;
  }
  if (state === 'missing') {
    emit('resolved', false);
  }
}, { immediate: true });

const orchestrator = useActivityOrchestrator();
const shellClosesActivity = useShellClosesActivity();
const underBackButton = useIaBackFrame();

// Inside a collection the collection owns the screen; an asset opened on its own takes it once started.
const { drawsOwnClose } = isInsideCollection.value
  ? { drawsOwnClose: computed(() => !shellClosesActivity) }
  : useActivityScreenClaim({
      ready: () => Boolean(asset.value),
      onOverview: () => screen.value === 'overview',
      shellClosesActivity,
    });

if (!orchestrator) {
  throw new Error('LearningAssetActivity must be used within an activity orchestrator');
}

const previewMode = orchestrator.previewMode ?? false;
const rewardsSuppressed = useRewardsSuppressed();
const contentComponent = computed(() => asset.value ? getLearningAssetContentComponent(asset.value.type) : null);

const { t } = useI18n();

// ─── This activity's own survey: asked after the slider, or by the coach once it is finished ─
const assetSurveyQuestions = computed(() =>
  (assetSurvey.value ? mapFeedbackSurveyQuestions(assetSurvey.value.questions) : []));

const {
  coinsAwarded: assetSurveyCoins,
  errorMessage: assetSurveyError,
  submitting: assetSurveySubmitting,
  handleSubmitted: handleAssetSurveySubmitted,
  handleSkipped: handleAssetSurveySkipped,
  handleAbandoned: handleAssetSurveyAbandoned,
} = useActivitySurveyScreen({
  place: computed(() => ({ learningAssetId: asset.value?.id ?? identifier.value })),
  questions: assetSurveyQuestions,
  isOpen: computed(() => screen.value === 'survey'),
  externalCoinsAwarded: assetSurveyCoinsAwarded,
  answeredInfo: coinsAwarded => ({
    learningAssetId: asset.value?.id ?? identifier.value,
    learningAssetName: asset.value?.name ?? null,
    coinsAwarded,
  }),
  onSkip: () => handleSurveyDone(),
});

const { submitLearningAssetFeedback } = useLearningAssetFeedback();
const { handleError } = useUnifiedErrorHandler();
const { isConsumerGhost, isGhostStudent, isLoggedIn } = useAuth();
const assetEventProps = computed(() => baseAssetEventProps());

// The completion screen draws a ghost's arm of the keep-learning experiment. The ghost exists once
// the content is up, so the arm is fetched here, ahead of that screen, and the exposure waits
// until the buttons are actually in front of them.
const ghostCompletionExperiment = useExperiment(GHOST_COMPLETION_EXPERIMENT);
watch(() => screen.value === 'content' && !previewMode && !!isConsumerGhost.value, (ready) => {
  if (ready) {
    void ghostCompletionExperiment.prefetch();
  }
}, { immediate: true });
const { $analytics } = useNuxtApp();

// Leaving content goes through cancelFlow: it walks back to the route the student came in
// from, and fires session-end recs in the background if the flow was started.
const handleBack = () => {
  orchestrator.cancelFlow();
};

const handleSentimentSubmit = async (sentiment: Sentiment) => {
  handleSentimentComplete(sentiment);

  if (previewMode) {
    return;
  }

  $analytics.track(EVENTS.UNIFIED_CONTENT_ASSET_SENTIMENT, {
    ...baseAssetEventProps(),
    sentiment,
    origin: props.activityArgs.origin ?? null,
  });

  const metadata: Record<string, string> = {};
  if (props.activityArgs.collectionId) {
    metadata.collectionId = props.activityArgs.collectionId;
  }
  if (props.activityArgs.collectionName) {
    metadata.collectionName = props.activityArgs.collectionName;
  }

  try {
    await submitLearningAssetFeedback(
      asset.value?.id ?? identifier.value,
      sentiment,
      Object.keys(metadata).length > 0 ? metadata : undefined,
    );
  }
  catch (err) {
    handleError(err, { logMessage: 'Failed to submit learning asset feedback', toast: t('unified.activity.sentiment.feedbackFailed') });
  }
};

const handleSentimentSkip = () => {
  handleSentimentComplete();
};
</script>

<template>
  <div class="relative flex h-full flex-col w-full">
    <div v-if="previewMode" class="absolute top-2 right-2 z-50">
      <Button
        size="sm"
        type="button"
        @click="handleCloseRequest"
      >
        {{ $t('unified.common.exitPreview') }}
      </Button>
    </div>
    <!--
      The testid names this layer's own X apart from the closes beneath it: inside a collection the
      asset overlays a credenza whose close carries the same accessible name, and a caller closing
      "the top one" has no other way to say which one that is.
    -->
    <KUIUnifiedCloseButton
      v-else-if="drawsOwnClose"
      data-testid="learning-asset-close-button"
      @close="handleCloseRequest"
    />
    <div v-if="screenState === 'loading'" class="flex flex-1 items-center justify-center">
      <KUICoreLoaderAnyway />
    </div>

    <template v-else-if="screenState === 'asset' && asset">
      <ActivitySurveyScreen
        v-if="screen === 'survey'"
        :questions="assetSurveyQuestions"
        :coins-awarded="assetSurveyCoins"
        :submitting="assetSurveySubmitting"
        :error-message="assetSurveyError"
        :is-ghost-user="!!isGhostStudent"
        :animations-enabled="accessibilityStore.animationsEnabled"
        :thanks-heading="t('unified.activity.completion.feedbackSurvey.thanksHeading')"
        testid-prefix="asset-survey"
        @submitted="handleAssetSurveySubmitted"
        @skipped="handleAssetSurveySkipped"
        @abandoned="handleAssetSurveyAbandoned"
        @close="handleSurveyDone"
      />

      <KUIUnifiedOverview
        v-else-if="screen === 'overview'"
        :flush-top="underBackButton"
        :title="asset.name"
        :description="asset.description"
        :duration="asset.duration"
        :coins="rewardsSuppressed ? null : asset.coins"
        :cover-image-url="asset.coverImageUrl"
        :learning-outcomes="asset.learningOutcomes"
        :has-structured-outcomes="!!asset.structuredLearningOutcome"
        :is-authenticated="isLoggedIn"
        terms-to=""
        :start-loading="starting"
        :spotlight="assetSpotlight"
        @start="handleBeginContent"
        @spotlight-link-click="trackSpotlightLinkClick"
      />

      <div
        v-else-if="screen === 'content'"
        data-vaul-no-drag
        class="flex h-full flex-col w-full"
      >
        <div v-if="completing" class="flex flex-1 items-center justify-center">
          <KUICoreLoaderAnyway />
        </div>
        <component
          :is="contentComponent"
          v-else
          :learning-asset-id="asset.id"
          :asset-name="asset.name"
          :asset-slug="asset.slug"
          :published-content="asset.publishedContent"
          :skills="asset.skills"
          :origin="props.activityArgs.origin"
          :learning-session-id="learningSessionId"
          @content-complete="(skillSlugs?: string[]) => handleContentComplete(skillSlugs)"
          @close="handleCloseRequest"
          @node-visited="(nodeId: string) => lastNodeId = nodeId"
        />
      </div>

      <KUIUnifiedSentiment
        v-else-if="screen === 'sentiment'"
        :title="$t('unified.activity.sentiment.howDidYouFind', { name: asset.name })"
        :mentor-name="activityArgs.mentor?.name ?? undefined"
        :mentor-picture-url="activityArgs.mentor?.mentorImageUrl ?? undefined"
        @submit="handleSentimentSubmit"
        @skip="handleSentimentSkip"
      />

      <LearningAssetCompletionScreen
        v-else-if="screen === 'completion'"
        :active="screen === 'completion'"
        :asset="asset"
        :asset-event-props="assetEventProps"
        :completion-result="completionResult"
        :collection-id="activityArgs.collectionId"
        :collection-name="activityArgs.collectionName"
        :collection-slug="activityArgs.collectionSlug"
        :origin="activityArgs.origin"
        @done="handleCompletionDone"
        @considerations-viewed="trackConsiderationsViewed"
        @clarity-viewed="trackClarityViewed"
      />
    </template>

    <template v-else>
      <KUIUnified404 @back="handleBack" />
      <KUICoreAlertMessage
        v-if="isDevMode && error"
        variant="error"
        :message="error.message"
        class="mx-8 mb-8"
      />
    </template>

    <UnifiedActivitiesActivityCloseConfirmation
      v-if="showCloseConfirmation"
      v-model:open="showCloseConfirmation"
      :nested="!!orchestrator?.isInsideCredenza"
      @confirm="confirmClose"
    />
  </div>
</template>
